/*

Class : StripePayments
Description : Tableau des paiements CB in-app (Stripe) pour le super admin :
montant TTC, CA client (montant perçu par le camping = TTC − frais totaux),
frais totaux prélevés au camping, frais Stripe réels et part nette PocketCamp
(totaux − Stripe) par transaction.
Rendu dans l'onglet "Paiements CB" de ConfigAdmin.
Author : Franck David

*/




import React, { Component } from 'react'
import Loader from '../UI/Loader'

class StripePayments extends Component {
	constructor(props) {
		super(props)
		this.state = {
			loading: true,
			payments: null,
			error: null
		}
	}

	componentDidMount() {
		this.props.fetchStripePayments()
		.then(payments => {
			if (Array.isArray(payments))
				this.setState({ loading: false, payments: payments })
			else if (payments && payments.error === 'stripe_not_configured')
				this.setState({ loading: false, payments: [], error: "Le paiement en ligne n'est pas configuré sur le serveur (clés Stripe manquantes)." })
			else
				this.setState({ loading: false, payments: [], error: "Impossible de récupérer la liste des paiements." })
		})
		.catch(() => {
			this.setState({ loading: false, payments: [], error: "Impossible de récupérer la liste des paiements." })
		})
	}

	// Formate un montant en centimes → "12,34 €" (ou "—" si inconnu).
	_eur = (cents) => {
		if (cents == null)
			return '—'
		return (cents / 100).toFixed(2).replace('.', ',') + ' €'
	}

	// Part nette PocketCamp = frais totaux − frais Stripe (null si l'un manque).
	_pocketcampFee = (payment) => {
		if (payment.total_fee == null || payment.stripe_fee == null)
			return null
		return payment.total_fee - payment.stripe_fee
	}

	// CA client = montant TTC perçu par le camping (montant − frais totaux).
	_clientRevenue = (payment) => {
		if (payment.amount == null || payment.total_fee == null)
			return null
		return payment.amount - payment.total_fee
	}

	_date = (timestamp) => {
		if (!timestamp)
			return '—'
		const date = new Date(timestamp * 1000)
		return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
	}

	render() {
		if (this.state.loading)
		{
			return (
				<div className="flex justify-center py-12">
					<Loader />
				</div>
			)
		}
		const payments = this.state.payments || []
		let total_amount = 0
		let total_client = 0
		let total_fees = 0
		let total_stripe = 0
		let total_pocketcamp = 0
		for (let i = 0; i < payments.length; i++)
		{
			total_amount += payments[i].amount || 0
			total_fees += payments[i].total_fee || 0
			total_stripe += payments[i].stripe_fee || 0
			const client = this._clientRevenue(payments[i])
			total_client += client || 0
			const net = this._pocketcampFee(payments[i])
			total_pocketcamp += net || 0
		}
		return (
			<div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
				<div className="px-5 py-4 border-b border-gray-100">
					<h2 className="text-lg font-bold text-gray-800" style={{ margin: 0 }}>Paiements CB in-app</h2>
					<p className="text-sm text-gray-400" style={{ margin: '0.25rem 0 0' }}>
						Transactions Stripe des {payments.length} dernières commandes payées par carte dans l'application.
					</p>
				</div>
				{this.state.error ? (
					<p className="px-5 py-4 text-sm font-medium text-red-600" style={{ margin: 0 }}>{this.state.error}</p>
				) : payments.length === 0 ? (
					<p className="px-5 py-10 text-center text-gray-400 text-sm" style={{ margin: 0 }}>Aucun paiement CB in-app pour le moment.</p>
				) : (
					<table className="w-full text-sm">
						<thead>
							<tr className="bg-gray-50 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">
								<th className="px-5 py-3">Date</th>
								<th className="px-5 py-3">Client</th>
								<th className="px-5 py-3 text-right">Montant TTC</th>
								<th className="px-5 py-3 text-right">CA client</th>
								<th className="px-5 py-3 text-right">Frais totaux</th>
								<th className="px-5 py-3 text-right">Frais Stripe</th>
								<th className="px-5 py-3 text-right">Frais PocketCamp</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-50">
							{payments.map(payment => (
								<tr key={payment.reservation} className="hover:bg-gray-50">
									<td className="px-5 py-3 text-gray-500 whitespace-nowrap">{this._date(payment.created_time)}</td>
									<td className="px-5 py-3">
										<span className="font-medium text-gray-800">{payment.identity != null ? payment.identity : '—'}</span>
										{payment.client_number != null && payment.client_number !== 0 ? (
											<span className="text-gray-400 ml-2">#{payment.client_number}</span>
										) : null}
										{payment.status && payment.status !== 'succeeded' ? (
											<span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">{payment.status}</span>
										) : null}
									</td>
									<td className="px-5 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">{this._eur(payment.amount)}</td>
									<td className="px-5 py-3 text-right text-emerald-600 font-medium whitespace-nowrap">{this._eur(this._clientRevenue(payment))}</td>
									<td className="px-5 py-3 text-right text-gray-700 font-medium whitespace-nowrap">{this._eur(payment.total_fee)}</td>
									<td className="px-5 py-3 text-right text-gray-500 whitespace-nowrap">{this._eur(payment.stripe_fee)}</td>
									<td className="px-5 py-3 text-right text-indigo-600 font-medium whitespace-nowrap">{this._eur(this._pocketcampFee(payment))}</td>
								</tr>
							))}
						</tbody>
						<tfoot>
							<tr className="bg-gray-50 font-semibold text-gray-900 border-t border-gray-200">
								<td className="px-5 py-3" colSpan={2}>Total</td>
								<td className="px-5 py-3 text-right whitespace-nowrap">{this._eur(total_amount)}</td>
								<td className="px-5 py-3 text-right text-emerald-600 whitespace-nowrap">{this._eur(total_client)}</td>
								<td className="px-5 py-3 text-right text-gray-700 whitespace-nowrap">{this._eur(total_fees)}</td>
								<td className="px-5 py-3 text-right text-gray-500 whitespace-nowrap">{this._eur(total_stripe)}</td>
								<td className="px-5 py-3 text-right text-indigo-600 whitespace-nowrap">{this._eur(total_pocketcamp)}</td>
							</tr>
						</tfoot>
					</table>
				)}
			</div>
		)
	}
}

export default StripePayments
